/**
 * The rollout context, its value shape and the pure resolver.
 *
 * Split out of `use-integration-rollout.tsx` so that module exports only the
 * provider and the hook: a file mixing a component with plain exports breaks
 * React Fast Refresh for everything importing those exports
 * (react-doctor/only-export-components).
 */
import { createContext } from 'react'
import {
  createIntegrationSourceGateway,
  type IntegrationSourceGateway,
} from 'v2/shared/services/integration-source-gateway'
import type { IntegrationSource } from 'v2/types/api/settings'

export interface IntegrationRolloutContextValue {
  gateway: IntegrationSourceGateway
  isResolved: boolean
  mode: IntegrationSource
  useAciIntegrations: boolean
}

interface IntegrationRolloutFlags {
  isInitialData?: boolean
  useAciIntegrations: boolean
}

/**
 * @param flags the raw feature flags
 * @param flagsFetchFailed the flag fetch failed outright — resolve to legacy
 *   rather than leaving the rollout unresolved forever. The flags object itself
 *   is left alone (`isInitialData` still says "unknown") so unrelated consumers
 *   do not read a hard "false" out of an outage.
 */
export const resolveIntegrationRollout = (
  flags: IntegrationRolloutFlags,
  flagsFetchFailed = false,
) => {
  const isResolved = flagsFetchFailed || flags.isInitialData !== true
  const useAciIntegrations = !flagsFetchFailed && isResolved && flags.useAciIntegrations === true
  const mode: IntegrationSource = useAciIntegrations ? 'aci' : 'nango'
  return { isResolved, mode, useAciIntegrations }
}

const legacyGateway = createIntegrationSourceGateway({ aciEnabled: false })
const initialValue: IntegrationRolloutContextValue = {
  gateway: legacyGateway,
  isResolved: true,
  mode: 'nango',
  useAciIntegrations: false,
}

/**
 * Exported so a caller can pin a rollout state directly — tests use it to render
 * a flag-on surface without standing up the whole auth + feature-flag stack.
 * Application code should use IntegrationRolloutProvider.
 */
export const IntegrationRolloutContext = createContext<IntegrationRolloutContextValue>(initialValue)
